import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
// @ts-ignore — packages/ui excluded from tsconfig.app.json
import { useOfflineManager } from '../../packages/ui/utils/offlineManager';

export interface QueuedAction {
  id: string;
  type: 'create' | 'update' | 'delete';
  entity: string;
  data: any;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
}

export const useOfflineStore = defineStore('offline', () => {
  // State
  const isOnline = ref(navigator.onLine);
  const syncInProgress = ref(false);
  const lastSyncTime = ref<Date | null>(null);
  const queuedActions = ref<QueuedAction[]>([]);
  const syncErrors = ref<string[]>([]);

  // Getters
  const hasQueuedActions = computed(() => queuedActions.value.length > 0);
  const queuedActionsCount = computed(() => queuedActions.value.length);
  const failedActions = computed(() => 
    queuedActions.value.filter(action => action.retryCount >= action.maxRetries)
  );
  const pendingActions = computed(() => 
    queuedActions.value.filter(action => action.retryCount < action.maxRetries)
  );

  // Actions
  const { queueAction, processSyncQueue, clearSyncQueue } = useOfflineManager();

  const addQueuedAction = (action: Omit<QueuedAction, 'id' | 'timestamp' | 'retryCount'>) => {
    const queuedAction: QueuedAction = {
      ...action,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: action.maxRetries || 3
    };
    
    queuedActions.value.push(queuedAction);
    
    // If online, try to process immediately
    if (isOnline.value) {
      processQueuedActions();
    }
  };

  const processQueuedActions = async () => {
    if (syncInProgress.value || !isOnline.value) return;
    
    syncInProgress.value = true;
    syncErrors.value = [];
    
    try {
      const actionsToProcess = [...queuedActions.value];
      
      for (const action of actionsToProcess) {
        try {
          await processAction(action);
          removeQueuedAction(action.id);
        } catch (error) {
          action.retryCount++;
          syncErrors.value.push(`Failed to sync ${action.entity}: ${error}`);
          
          if (action.retryCount >= action.maxRetries) {
            console.error(`Action ${action.id} failed after ${action.maxRetries} retries`);
          }
        }
      }
      
      if (queuedActions.value.length === 0) {
        lastSyncTime.value = new Date();
      }
    } catch (error) {
      console.error('Error processing queued actions:', error);
    } finally {
      syncInProgress.value = false;
    }
  };

  const processAction = async (action: QueuedAction) => {
    // This would integrate with the actual sync logic
    // For now, we'll simulate the process
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Call the offline manager's queueAction
    await queueAction({
      type: action.type,
      entity: action.entity,
      data: action.data,
      timestamp: action.timestamp
    });
  };

  const removeQueuedAction = (actionId: string) => {
    const index = queuedActions.value.findIndex(action => action.id === actionId);
    if (index !== -1) {
      queuedActions.value.splice(index, 1);
    }
  };

  const retryFailedActions = () => {
    failedActions.value.forEach(action => {
      action.retryCount = 0;
    });
    processQueuedActions();
  };

  const clearAllQueuedActions = () => {
    queuedActions.value = [];
    syncErrors.value = [];
    clearSyncQueue();
  };

  const updateOnlineStatus = (online: boolean) => {
    isOnline.value = online;
    if (online && hasQueuedActions.value) {
      processQueuedActions();
    }
  };

  const clearSyncErrors = () => {
    syncErrors.value = [];
  };

  const reset = () => {
    isOnline.value = navigator.onLine;
    syncInProgress.value = false;
    lastSyncTime.value = null;
    queuedActions.value = [];
    syncErrors.value = [];
  };

  return {
    // State
    isOnline,
    syncInProgress,
    lastSyncTime,
    queuedActions,
    syncErrors,
    
    // Getters
    hasQueuedActions,
    queuedActionsCount,
    failedActions,
    pendingActions,
    
    // Actions
    addQueuedAction,
    processQueuedActions,
    removeQueuedAction,
    retryFailedActions,
    clearAllQueuedActions,
    updateOnlineStatus,
    clearSyncErrors,
    reset
  };
});
